/**
 * Lot & Elevation DOM Component (Floor Plan)
 *
 * Renders the Lot & Elevation content panel for floor plan detail.
 *
 * @module nativeInteractiveSiteMap/components/lotAndElevationFloorPlan.dom
 */

import fastdom from 'fastdom';
import { backButtonIconSvg, informationIconSvg } from 'constants/svg-assets';
import getLotGroupCardHtml from './lotGroupCard.dom';
import createElevationSelectorDom from './elevationSelector.dom';

/**
 * Creates the Lot & Elevation content component for floor plan detail.
 * @param {Object} detailItem - The currently selected floor plan
 * @param {Object} lot - The currently selected lot
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createLotAndElevationFloorPlanDom(detailItem = {}, lot = {}) {
  const state = { root: null, elevationSelector: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = [
      'nhs-c-native-interactive-site-map__lot-elevation',
    ].join(' ');

    // Use backend-formatted presentation field for consistency with formattedPrice.
    const size = lot?.formattedLotSize ?? null;
    const imageSrc = detailItem?.image || null; // Use actual image URL if available
    const name = detailItem?.name || null; // Use actual name if available
    const lotNumber = lot?.displayLabel ? `Lot ${lot.displayLabel}` : null;
    const group = lot?.lotGroupName ? `- Group ${lot.lotGroupName}` : null;
    const type = (lot?.lotType && lot.lotType !== 'None') ? lot.lotType : null;
    const facing = (lot?.lotFacing && lot.lotFacing !== 'N/A') ? lot.lotFacing : null;
    const elevations = detailItem?.elevations ?? [];
    const hasElevations = elevations.length > 0;

    state.root.innerHTML = `
      <div class="nhs-u-padding--0-500 nhs-u-flex-column nhs-u-gap--500">
        <!-- Placeholder image for Lot & Elevation -->

        <div class="nhs-c-lotelevation__image-wrapper nhs-mq-lotelevation__image-wrapper-large">
          <img data-siteplan-card-image
            class="nhs-c-lotelevation__image"
            src="${imageSrc}"
            alt="${name}">
        </div>

        <!-- Header with title and "Change Lot" button -->
        <div class="nhs-u-display--flex nhs-u-justify-content-space-between nhs-u-items-center nhs-u-padding--200-0">

          <h2 class="nhs-u-display--flex nhs-u-items-center nhs-u-gap--100">
            <span class="nhs-o-typography--sans-body-1-medium-regular">
              Selected lot
            </span>
            <span class="nhs-c-lotelevation__tooltip nhs-mq-lotelevation__tooltip-large nhs-u-color--gray-700 nhs-u-cursor--pointer nhs-u-hide-mobile">
              ${informationIconSvg}
              <span class="nhs-c-lotelevation__tooltip-text">
                Select a lot using the map
              </span>
            </span>
          </h2>

          <div class="nhs-c-lotelevation__change-lot nhs-mq-lotelevation__change-lot-medium nhs-u-cursor--pointer nhs-u-color--teal-1000 nhs-u-gap--100 nhs-o-typography--sans-label-1-extra-extra-small-regular">
            <span>Change Lot</span>
            ${backButtonIconSvg}
          </div>
        </div>

        <!-- Lot Details Card -->
        ${getLotGroupCardHtml({ lotNumber, group, size, type, facing })}

        ${hasElevations ? '<div class="nhs-c-lot-home-features-divider nhs-u-width--full"></div>' : ''}

        <div data-elevation-selector-slot></div>

        <div class="nhs-u-border-top--1 nhs-u-border-color--neutral-300 nhs-u-padding-top--100"></div>
      </div>
    `;

    state.elevationSelector = createElevationSelectorDom(elevations);
    const elevationSelectorElement = state.elevationSelector.getElement();
    if (elevationSelectorElement) {
      state.root.querySelector('[data-elevation-selector-slot]')?.appendChild(elevationSelectorElement);
    }
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      state.elevationSelector?.remove();
      state.elevationSelector = null;
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
